import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance'
import { Field, FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'
import CustomInput from '../../../components/common/CustomInput'
import CustomTextarea from '../../../components/common/CustomTextarea'
import CustomSelect from '../../../components/common/CustomSelect'
import CustomCheckBox from '../../../components/common/CustomCheckBox'

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
  description: Yup.string().required('Description is required'),
  priority: Yup.string().required('Priority is required'),
  dueDate: Yup.string().required('Due Date is required'),
  assignTo: Yup.string().required('Assign To is required'),
  company: Yup.string().required('Company is required'),
  bigButton: Yup.boolean(),
  bigTaskType: Yup.string().nullable().when('bigButton', {
    is: true,
    then: (s) => s.required('Please select a Big Task Type'),
    otherwise: (s) => s.nullable(),
  }),
  bigTaskStatus: Yup.string().nullable().when('bigButton', {
    is: true,
    then: (s) => s.required('Please select a Big Task Status'),
    otherwise: (s) => s.nullable(),
  }),
})

const TaskReopen = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const from = location.state?.from || '/task'

  const [loading, setLoading] = useState(true)
  const [submitted, setSubmitted] = useState(false)
  const [TeamOptions, setTeamOptions] = useState([])
  const [companyOptions, setCompanyOptions] = useState([])
  const [bigTaskTypeOptions, setBigTaskTypeOptions] = useState([])
  const [bigTaskStatusOptions, setBigTaskStatusOptions] = useState([])

  const formik = useFormik({
    onSubmit: (values) => handleSubmit(values),
    validationSchema,
    initialValues: {
      name: '',
      description: '',
      priority: '',
      assignTo: '',
      dueDate: '',
      company: '',
      paidButton: false,
      bigButton: false,
      printButton: false,
      bigTaskType: null,
      bigTaskStatus: null,
    },
  })

  const fetchDropdowns = async () => {
    const [teamRes, companyRes, bigTypeRes, bigStatusRes] = await Promise.all([
      axiosAuthInstance.get('team/dropdown'),
      axiosAuthInstance.get('company/dropdown'),
      axiosAuthInstance.get('big-task-type/dropdown'),
      axiosAuthInstance.get('big-task-status/dropdown'),
    ])

    setTeamOptions(teamRes.data.result.map(i => ({ value: i._id, label: `${i.full_name} (${i.role})` })))
    setCompanyOptions(companyRes.data.result.map(i => ({ value: i._id, label: i.name })))
    setBigTaskTypeOptions(bigTypeRes.data.result.map(i => ({ value: i._id, label: i.name })))
    setBigTaskStatusOptions(bigStatusRes.data.result.map(i => ({ value: i._id, label: i.name })))
  }

  const fetchData = async () => {
    const res = await axiosAuthInstance.get(`task/detail/${id}`)
    const task = res.data.result

    formik.setValues({
      name: task.name || '',
      description: task.description || '',
      priority: task.priority || '',
      dueDate: task.dueDate?.split('T')[0] || '',
      assignTo: task.assignTo?._id || '',
      company: task.company?._id || '',
      paidButton: task.paidButton || false,
      bigButton: task.bigButton || false,
      printButton: task.printButton || false,
      bigTaskType: task.bigTaskType?._id || null,
      bigTaskStatus: task.bigTaskStatus?._id || null,
    })

    setLoading(false)
  }

  const handleSubmit = async (values) => {
    setSubmitted(true)
    try {
      await axiosAuthInstance.put(`task/update/${id}`, values)
      await axiosAuthInstance.put(`task/reopen/${id}`, { dueDate: values.dueDate })
      navigate(from)
    } catch (error) {
      console.error('Error reopening task', error)
    } finally {
      setSubmitted(false)
    }
  }

  useEffect(() => {
    fetchDropdowns()
    fetchData()
  }, [id])

  if (loading) return <div>Loading...</div>

  return (
    <FormikProvider value={formik}>
      <form className="flex flex-col p-4 gap-4" onSubmit={formik.handleSubmit}>
        <Field name="name" label="Task Name" component={CustomInput} />
        <Field name="description" label="Description" component={CustomTextarea} />
        <Field name="company" label="Company" component={CustomSelect} options={companyOptions} />
        <Field name="priority" label="Priority" component={CustomSelect} options={[
          { value: 'Low', label: 'Low' },
          { value: 'Medium', label: 'Medium' },
          { value: 'High', label: 'High' },
          { value: 'Emergency', label: 'Emergency' },
        ]} />
        <Field name="dueDate" label="New Due Date" type="date" component={CustomInput} />
        <Field name="assignTo" label="Assign To" component={CustomSelect} options={TeamOptions} />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field name="paidButton" label="Paid Task" component={CustomCheckBox} />
          <Field name="bigButton" label="Big Task" component={CustomCheckBox} />
          <Field name="printButton" label="Printing Task" component={CustomCheckBox} />
        </div>

        {formik.values.bigButton && (
          <div className="border rounded p-4">
            <Field name="bigTaskType" label="Big Task Type" component={CustomSelect} options={bigTaskTypeOptions} />
            <Field name="bigTaskStatus" label="Big Task Status" component={CustomSelect} options={bigTaskStatusOptions} />
          </div>
        )}

        <button type="submit" disabled={submitted} className="bg-orange-500 text-white px-6 py-2 rounded">
          {submitted ? 'Processing...' : 'Update and Reopen Task'}
        </button>
      </form>
    </FormikProvider>
  )
}

export default TaskReopen
